import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Compact screenshot collector — paste from clipboard or pick a file,
 * client-side resize to ≤1200px wide, JPEG-encode at ~0.85 quality, emit
 * the resulting `data:image/jpeg;base64,…` URL.
 *
 * Why data URLs (not a real upload endpoint): the volume is tiny (≤6
 * per lesson, ≤500KB each), authors are super-users, and storing JSON
 * on the lesson row sidesteps file ownership, signed URLs, retention,
 * and S3 plumbing entirely. We can always migrate to real blobs later
 * by reading the data URLs out of `gable_lessons.screenshot_data_urls`.
 *
 * The component is presentational — it doesn't talk to the backend.
 * Two-way `value` binding holds the current `string[]` of data URLs;
 * the parent persists them when it saves the lesson.
 */
@Component({
    selector: 'app-gable-screenshot-uploader',
    templateUrl: './gable-screenshot-uploader.component.html',
    styleUrls: ['./gable-screenshot-uploader.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
})
export class GableScreenshotUploaderComponent {
    /** Current array of data URLs. */
    @Input() value: string[] = [];
    @Output() readonly valueChange = new EventEmitter<string[]>();

    /**
     * Hard cap — kept tight (default 2) because each upload costs one
     * Claude vision call on save. Most lesson screenshots are one image
     * showing the broken answer; two for before/after comparisons.
     */
    @Input() maxImages = 2;

    /** Resize ceiling for the longer image side. */
    @Input() maxWidth = 1200;

    /** JPEG quality for re-encoded screenshots. 0.85 is a fine balance. */
    @Input() jpegQuality = 0.85;

    isDraggingOver = false;
    isProcessing = false;
    errorMessage: string | null = null;

    constructor(private readonly _cdr: ChangeDetectorRef) {}

    get canAddMore(): boolean {
        return this.value.length < this.maxImages;
    }

    // ----- Paste handler — bind once on host so paste anywhere inside the
    // uploader (or anywhere on the page while the drawer is open) lands here.

    @HostListener('paste', ['$event'])
    onHostPaste(event: ClipboardEvent): void {
        this.handleClipboard(event);
    }

    handleClipboard(event: ClipboardEvent): void {
        if (!event.clipboardData) { return; }
        const items = Array.from(event.clipboardData.items);
        const imageItem = items.find((i) => i.type.startsWith('image/'));
        if (!imageItem) { return; }
        const file = imageItem.getAsFile();
        if (file) {
            event.preventDefault();
            this.ingestFiles([file]);
        }
    }

    // ----- File picker

    onFilePicked(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) { return; }
        this.ingestFiles(Array.from(input.files));
        input.value = ''; // reset so picking the same file twice still fires change
    }

    // ----- Drag-and-drop

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDraggingOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDraggingOver = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDraggingOver = false;
        if (!event.dataTransfer?.files?.length) { return; }
        this.ingestFiles(Array.from(event.dataTransfer.files));
    }

    onRemove(index: number): void {
        const next = this.value.filter((_, i) => i !== index);
        this.value = next;
        this.valueChange.emit(next);
    }

    trackByIndex(idx: number): number {
        return idx;
    }

    // -------------------------------------------------------------------------

    private ingestFiles(files: File[]): void {
        const remaining = this.maxImages - this.value.length;
        const toProcess = files.filter((f) => f.type.startsWith('image/')).slice(0, remaining);
        if (toProcess.length === 0) { return; }

        this.isProcessing = true;
        this.errorMessage = null;
        this._cdr.markForCheck();

        Promise.all(toProcess.map((f) => this.fileToCompressedDataUrl(f)))
            .then((dataUrls) => {
                const next = [...this.value, ...dataUrls];
                this.value = next;
                this.valueChange.emit(next);
            })
            .catch((err: unknown) => {
                this.errorMessage = err instanceof Error
                    ? err.message
                    : 'Could not read one of the images.';
            })
            .finally(() => {
                this.isProcessing = false;
                this._cdr.markForCheck();
            });
    }

    /**
     * Reads a File, draws it to a <canvas> resized so the longer side ≤
     * `maxWidth`, re-encodes as JPEG at `jpegQuality`, returns the data URL.
     * Smaller than the original PNG in almost every case; the JPEG quality
     * loss is invisible for screenshots of UI.
     */
    private fileToCompressedDataUrl(file: File): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                img.onload = () => {
                    const scale = Math.min(1, this.maxWidth / Math.max(img.width, img.height));
                    const w = Math.round(img.width * scale);
                    const h = Math.round(img.height * scale);

                    const canvas = document.createElement('canvas');
                    canvas.width = w;
                    canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Canvas unsupported'));
                        return;
                    }
                    ctx.drawImage(img, 0, 0, w, h);
                    try {
                        const dataUrl = canvas.toDataURL('image/jpeg', this.jpegQuality);
                        resolve(dataUrl);
                    } catch (err) {
                        reject(err instanceof Error ? err : new Error('Encode failed'));
                    }
                };
                img.onerror = () => reject(new Error('Image failed to load'));
                img.src = String(reader.result);
            };
            reader.onerror = () => reject(new Error('File read failed'));
            reader.readAsDataURL(file);
        });
    }
}
